import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import FormInput from '../../shared/ui/form/FormInput';
import Button from '../../shared/ui/button/Button';
import { GoogleLoginButton } from '../../components';
import { message } from 'antd';
import './AuthPages.css';

interface LocationState {
  from?: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, login, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 获取重定向路径
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get('returnUrl');
  const from = returnUrl || (location.state as LocationState)?.from || '/';

  // 如果已经登录，重定向到目标页面
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [state.isAuthenticated, navigate, from]);

  // 清除错误信息
  useEffect(() => {
    if (state.error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.error, clearError]);

  // 表单验证
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('auth.registerPage.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('auth.registerPage.validation.emailInvalid');
    }

    if (!formData.password) {
      errors.password = t('auth.registerPage.validation.passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('auth.registerPage.validation.passwordMinLength');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // 清除对应字段的错误
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await login(formData.email, formData.password);
      // 登录成功后会通过useEffect重定向
    } catch (error) {
      // 错误信息已经在AuthContext中处理
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">{t('auth.loginPage.title')}</h1>
          <p className="auth-subtitle">{t('auth.loginPage.subtitle')}</p>
        </div>

        {state.error && (
          <div className="auth-error">
            <span className="auth-error-icon">⚠️</span>
            {state.error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <FormInput
            id="email"
            name="email"
            type="email"
            label={t('auth.email')}
            value={formData.email}
            onChange={handleInputChange}
            error={formErrors.email}
            placeholder={t('auth.loginPage.emailPlaceholder')}
            icon={<span>📧</span>}
            fullWidth
            required
          />

          <FormInput
            id="password"
            name="password"
            type="password"
            label={t('auth.password')}
            value={formData.password}
            onChange={handleInputChange}
            error={formErrors.password}
            placeholder={t('auth.loginPage.passwordPlaceholder')}
            icon={<span>🔒</span>}
            fullWidth
            required
          />

          <div className="auth-actions">
            <Button
              htmlType="submit"
              buttonVariant="primary"
              size="large"
              fullWidth
              loading={isSubmitting || state.loading}
              disabled={isSubmitting || state.loading}
            >
              {isSubmitting || state.loading ? t('common.loading') : t('auth.loginPage.loginButton')}
            </Button>
          </div>
        </form>

        <div className="auth-divider">
          <span>{t('common.or')}</span>
        </div>

        <div className="auth-social">
          <GoogleLoginButton
            text={t('auth.loginPage.googleLogin')}
            returnUrl={from}
            onSuccess={() => {
              message.success(t('auth.loginPage.loginSuccess'));
              // 登录成功后会通过useEffect重定向
            }}
            onError={(error) => {
              message.error(error);
            }}
          />
        </div>

        <div className="auth-footer">
          <p className="auth-footer-text">
            {t('auth.loginPage.noAccount')}
            <Link to="/register" className="auth-link">
              {t('auth.loginPage.registerLink')}
            </Link>
          </p>
          
          <Link to="/forgot-password" className="auth-link auth-link--secondary">
            {t('auth.loginPage.forgotPasswordLink')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 